import React, { useRef, useState, useEffect, useCallback } from 'react';

const StickyScrollbar = ({ children }) => {
    const wrapperRef = useRef(null);
    const scrollbarRef = useRef(null);
    const innerRef = useRef(null);
    const [showBar, setShowBar] = useState(false);
    const syncingRef = useRef(false);
    const tableWrapperRef = useRef(null);

    const getTableWrapper = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return null;
        return wrapper.querySelector('.p-datatable-wrapper')
            || wrapper.querySelector('.p-datatable-scrollable-body');
    }, []);

    // Atualiza posição e largura da scrollbar fixa
    const updateBarPosition = useCallback(() => {
        const tableWrapper = tableWrapperRef.current;
        const barEl = scrollbarRef.current;
        if (!tableWrapper || !barEl) return;

        const rect = tableWrapper.getBoundingClientRect();
        barEl.style.left = rect.left + 'px';
        barEl.style.width = rect.width + 'px';
    }, []);

    // Sincroniza largura interna e verifica overflow
    const syncWidths = useCallback(() => {
        const tableWrapper = tableWrapperRef.current;
        if (!tableWrapper) return;

        const scrollWidth = tableWrapper.scrollWidth;
        const clientWidth = tableWrapper.clientWidth;
        const hasOverflow = scrollWidth > clientWidth;

        // Verifica se a tabela está visível na viewport
        const rect = tableWrapper.getBoundingClientRect();
        const tableTopVisible = rect.top < window.innerHeight;
        const tableInViewport = tableTopVisible && rect.bottom > 0;

        // Verifica se a scrollbar original (bottom da tabela) já está visível na viewport
        const nativeScrollbarVisible = rect.bottom <= window.innerHeight;

        // Mostra a scrollbar fixa se tem overflow, a tabela está visível,
        // mas a scrollbar original ainda não está visível
        setShowBar(hasOverflow && tableInViewport && !nativeScrollbarVisible);

        if (innerRef.current) {
            innerRef.current.style.width = scrollWidth + 'px';
        }

        updateBarPosition();
    }, [updateBarPosition]);

    useEffect(() => {
        const tableWrapper = getTableWrapper();
        if (!tableWrapper) return;
        tableWrapperRef.current = tableWrapper;

        // Sincroniza scroll bidirecional
        const onTableScroll = () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            if (scrollbarRef.current) {
                scrollbarRef.current.scrollLeft = tableWrapper.scrollLeft;
            }
            syncingRef.current = false;
        };

        const onBarScroll = () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            tableWrapper.scrollLeft = scrollbarRef.current.scrollLeft;
            syncingRef.current = false;
        };

        const onScrollOrResize = () => {
            syncWidths();
        };

        tableWrapper.addEventListener('scroll', onTableScroll);
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });

        const barEl = scrollbarRef.current;
        if (barEl) {
            barEl.addEventListener('scroll', onBarScroll);
        }

        // Observa mudanças de tamanho da tabela
        const resizeObserver = new ResizeObserver(onScrollOrResize);
        resizeObserver.observe(tableWrapper);

        // Executa sincronização inicial
        syncWidths();

        return () => {
            tableWrapper.removeEventListener('scroll', onTableScroll);
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('resize', onScrollOrResize);
            if (barEl) {
                barEl.removeEventListener('scroll', onBarScroll);
            }
            resizeObserver.disconnect();
        };
    }, [getTableWrapper, syncWidths]);

    // Reconecta evento de scroll quando a barra aparece/desaparece
    useEffect(() => {
        const barEl = scrollbarRef.current;
        const tableWrapper = tableWrapperRef.current;
        if (!barEl || !tableWrapper) return;

        const onBarScroll = () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            tableWrapper.scrollLeft = barEl.scrollLeft;
            syncingRef.current = false;
        };

        barEl.addEventListener('scroll', onBarScroll);
        // Sincroniza posição atual
        barEl.scrollLeft = tableWrapper.scrollLeft;
        updateBarPosition();

        return () => {
            barEl.removeEventListener('scroll', onBarScroll);
        };
    }, [showBar, updateBarPosition]);

    return (
        <div ref={wrapperRef} className="sticky-scrollbar-wrapper">
            {children}
            {showBar && (
                <div ref={scrollbarRef} className="sticky-scrollbar-fixed">
                    <div ref={innerRef} className="sticky-scrollbar-inner" />
                </div>
            )}
        </div>
    );
};

export { StickyScrollbar };
